import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  BackArrow,
  Input,
  Button,
  TopProgress,
  CheckBox,
  InfoBlock,
} from '../../../../../components'
import {
  Container,
  TitleBig,
  CreateContainer,
} from './styles'
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useUi } from '../../../../../context/ui.context'
import { useTheme } from 'styled-components'
import { useAuth } from '../../../../../context/user.context'
import {openStackService} from '../../../../../api'
import { icons } from '../../../../../icons'

type ActionType = 'sync' | 'test-connection' | 'get-openrc'

const NotificationSettings: FC = () => {
  const navigate = useNavigate()
  const [t] = useTranslation()
  const {viewAlert} = useUi()
  const {user} = useAuth()
  const theme:any = useTheme()
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [settings, setSettings] = useState<any>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)

  const initialUrl = {
    url: '',
    topic: ['notifications'],
    exchange: ['keystone', 'heat', 'openstack', 'nova', 'neutron', 'cinder', 'glance'],
    pool: '',
    amqp_durable_queues: false,
    rabbit_quorum_queue: false,
  }

  const initialRegionNotification = {
    region: '',
    urls: [initialUrl],
  }

  const initialValues = {
    amqp_durable_queues: false,
    regions: [],
    regions_notifications_settings: [initialRegionNotification],
  }

  const onSubmit = async (values:any, action:ActionType) => {
    setLoading(true)
    try {
      if (action === 'sync') {
        const response = await openStackService.updateNotificationSettings(values)
        viewAlert({
          severity: 'success',
          message: t('SETTINGS_SAVED_SUCCESSFULLY'),
        })
        setLoadingComplete(true)
        setTimeout(() => setLoadingComplete(false), 2000)
      } else if (action === 'test-connection') {
        // Handle test connection logic here if needed
        viewAlert({
          severity: 'info',
          message: t('TEST_CONNECTION_INITIATED'),
        })
      }
    } catch (e:any) {
      viewAlert({
        severity: 'error',
        message: e?.response?.data?.detail || t('ERROR_SAVING_SETTINGS'),
      })
    } finally {
      setLoading(false)
    }
  }

  const {
    values,
    errors,
    touched,
    setErrors,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    validationSchema:yup.object().shape({
      amqp_durable_queues: yup.bool(),
      regions: yup.array().of(yup.string()),
      regions_notifications_settings: yup.array().of(yup.object().shape({
        region: yup.string(),
        urls: yup.array().of(yup.object().shape({
          url: yup.string(),
          topic: yup.array().of(yup.string()),
          exchange: yup.array().of(yup.string()),
          pool: yup.string(),
          amqp_durable_queues: yup.bool(),
          rabbit_quorum_queue: yup.bool(),
        })).min(1, `${t('At least one URL is required')}`),
      })),
    }),
    initialValues,
    onSubmit:(values:any, { setSubmitting }) => {
      onSubmit(values, 'sync')
      setSubmitting(false)
    },
  })

  const formikProps = {
    errors,
    values,
    touched,
    handleBlur,
  }

  const handleInputChange = (index: number, field: string, value: any, nestedIndex?: number, nestedField?: string) => {
    const updatedSettings = values.regions_notifications_settings.map((setting: any, i: number) => {
      if (i !== index) return setting
      if (nestedIndex !== undefined && nestedField) {
        return {
          ...setting,
          urls: setting.urls.map((url: any, j: number) =>
            j === nestedIndex ? { ...url, [nestedField]: value } : url
          ),
        }
      }
      return { ...setting, [field]: value }
    })
    setFieldValue('regions_notifications_settings', updatedSettings)
  }

  const handleArrayInputChange = (regionIndex: number, urlIndex: number, field: string, value: string) => {
    const parsed = value.split(',').map((item: string) => item.trim()).filter((item: string) => item)
    const updatedSettings = values.regions_notifications_settings.map((setting: any, i: number) => {
      if (i !== regionIndex) return setting
      return {
        ...setting,
        urls: setting.urls.map((url: any, j: number) =>
          j === urlIndex ? { ...url, [field]: parsed } : url
        ),
      }
    })
    setFieldValue('regions_notifications_settings', updatedSettings)
  }

  const addRegionSetting = () => {
    setFieldValue('regions_notifications_settings', [
      ...values.regions_notifications_settings,
      { ...initialRegionNotification, urls: [{ ...initialUrl }] },
    ])
  }

  const removeRegionSetting = (index: number) => {
    setFieldValue(
      'regions_notifications_settings',
      values.regions_notifications_settings.filter((_: any, i: number) => i !== index),
    )
  }

  const addUrl = (regionIndex: number) => {
    const updatedSettings = values.regions_notifications_settings.map((setting: any, i: number) => {
      if (i !== regionIndex) return setting
      return { ...setting, urls: [...setting.urls, { ...initialUrl }] }
    })
    setFieldValue('regions_notifications_settings', updatedSettings)
  }

  const removeUrl = (regionIndex: number, urlIndex: number) => {
    const updatedSettings = values.regions_notifications_settings.map((setting: any, i: number) => {
      if (i !== regionIndex) return setting
      return { ...setting, urls: setting.urls.filter((_: any, j: number) => j !== urlIndex) }
    })
    setFieldValue('regions_notifications_settings', updatedSettings)
  }

  const getData = async () => {
    try {
      const res = await openStackService.getNotificationSettings()
      setSettings(res.data)
      
      // Set the main amqp_durable_queues value
      setFieldValue('amqp_durable_queues', res.data.amqp_durable_queues || false)
      
      // Set the regions array
      setFieldValue('regions', res.data.regions || [])
      
      // Set the regions_notifications_settings
      if (res.data.regions_notifications_settings && res.data.regions_notifications_settings.length > 0) {
        setFieldValue('regions_notifications_settings', res.data.regions_notifications_settings)
      } else {
        setFieldValue('regions_notifications_settings', [initialRegionNotification])
      }
      
      setPageLoading(false)
    } catch (e:any) {
      viewAlert({
        severity: 'error',
        message: e?.response?.data?.detail || t('ERROR'),
      })
    }
  }

  useEffect(() => {
    getData()
  }, [])

  if (pageLoading) return <TopProgress loading={pageLoading}/>

  return (
    <CreateContainer>
      <Container style={{minWidth:'550px', maxWidth:'550px'}}>
        <div>
          <Input
            fieldName="regions"
            title={t("REGIONS")}
            placeholder={t("Enter regions separated by commas")}
            value={values.regions.join(',')}
            handleChange={(e:any) => setFieldValue('regions', e.target.value.split(',').map((item: string) => item.trim()).filter((item: string) => item))}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            values={values}
          />
        </div>
        {values.regions_notifications_settings.map((setting:any, index:number) => (
          <InfoBlock
            key={index}
            data={[]}
            title={setting.region || `Notification Setting ${index + 1}`}
            dropped
            customDropped={
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '20px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => removeRegionSetting(index)}
                    variant='icon'
                    size='small'
                    title={t("DELETE")}
                    disabled={values.regions_notifications_settings.length <= 1}
                  >
                    <img src={icons.DeleteIcon} alt="delete" />
                  </Button>
                </div>
                <Input
                  required
                  fieldName={`regions_notifications_settings[${index}].region`}
                  title={t("REGION")}
                  placeholder={t("Enter region name")}
                  errors={errors}
                  touched={touched}
                  values={values}
                  value={setting.region}
                  handleChange={(e:any) => handleInputChange(index, 'region', e.target.value)}
                />
                {setting.urls.map((url:any, urlIndex:number) => (
                  <div key={urlIndex} style={{ paddingLeft: '20px', borderLeft: '2px solid #ccc', marginBottom: '20px', position: 'relative' }}>
                    {setting.urls.length > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          onClick={() => removeUrl(index, urlIndex)}
                          variant='icon'
                          size='small'
                          title={t("DELETE")}
                        >
                          <img src={icons.DeleteIcon} alt="delete" />
                        </Button>
                      </div>
                    )}
                    <Input
                      required
                      fieldName={`regions_notifications_settings[${index}].urls[${urlIndex}].url`}
                      title={t("URL")}
                      placeholder={t("Enter notification URL")}
                      errors={errors}
                      touched={touched}
                      values={values}
                      value={url.url}
                      handleChange={(e:any) => handleInputChange(index, 'url', e.target.value, urlIndex, 'url')}
                      toolTip
                      toolTipText={`${t("NOTIFICATIONS_URLS_TOOLTIP")}`}
                    />
                    <Input
                      fieldName={`regions_notifications_settings[${index}].urls[${urlIndex}].topic`}
                      title={t("TOPIC")}
                      placeholder={t("Enter topics separated by commas")}
                      value={url.topic.join(',')}
                      handleChange={(e:any) => handleArrayInputChange(index, urlIndex, 'topic', e.target.value)}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      values={values}
                      toolTip
                      toolTipText={t("NOTIFICATIONS_TOPIC_TOOLTIP")}
                    />
                    <Input
                      fieldName={`regions_notifications_settings[${index}].urls[${urlIndex}].exchange`}
                      title={t("EXCHANGE")}
                      placeholder={t("Enter exchanges separated by commas")}
                      value={url.exchange.join(',')}
                      handleChange={(e:any) => handleArrayInputChange(index, urlIndex, 'exchange', e.target.value)}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      values={values}
                      toolTip
                      toolTipText={t("NOTIFICATIONS_EXCHANGE_TOOLTIP")}
                    />
                    <Input
                      required
                      fieldName={`regions_notifications_settings[${index}].urls[${urlIndex}].pool`}
                      title={t("POOL")}
                      placeholder={t("Enter pool name")}
                      errors={errors}
                      touched={touched}
                      values={values}
                      value={url.pool}
                      handleChange={(e:any) => handleInputChange(index, 'pool', e.target.value, urlIndex, 'pool')}
                      toolTip
                      toolTipText={t("NOTIFICATIONS_POOL_NAME_TOOLTIP")}
                    />
                    <CheckBox
                      value={url.amqp_durable_queues}
                      onChange={() => setFieldValue(`regions_notifications_settings[${index}].urls[${urlIndex}].amqp_durable_queues`, !url.amqp_durable_queues)}
                      title={t("AMQP_DURABLE_QUEUES")}
                      toolTip
                      toolTipText={t("AMQP_DURABLE_QUEUES_TOOLTIP")}
                    />
                    <CheckBox
                      value={url.rabbit_quorum_queue}
                      onChange={() => setFieldValue(`regions_notifications_settings[${index}].urls[${urlIndex}].rabbit_quorum_queue`, !url.rabbit_quorum_queue)}
                      title={t("RABBIT_QUORUM_QUEUE")}
                    />
                  </div>
                ))}
                <div>
                  <Button
                    onClick={() => addUrl(index)}
                    variant='secondary'
                    size='small'
                    title={t("ADD_URL")}
                  />
                </div>
              </div>
            }
          />
        ))}
        <div>
          <Button
            onClick={addRegionSetting}
            variant='secondary'
            size='display'
            title={t("ADD_REGION_NOTIFICATION")}
          />
        </div>
        <CheckBox
          value={values.amqp_durable_queues}
          onChange={() => setFieldValue('amqp_durable_queues', !values.amqp_durable_queues)}
          title={t("AMQP_DURABLE_QUEUES")}
          toolTip
          toolTipText={t("AMQP_DURABLE_QUEUES_TOOLTIP")}
        />
        <div style={{display:'flex', marginTop:'0px'}}>
          <Button
            variant="secondary"
            size="display"
            customStyles={{marginRight:"10px"}}
            onClick={() => onSubmit(values, 'test-connection')}
            title={t("TEST_CONNECTION")}
            loading={loading}
            loadingComplete={loadingComplete}
          />
          <Button
            variant="primary"
            size="display"
            customStyles={{marginRight:"10px"}}
            onClick={() => handleSubmit()}
            title={t("SAVE")}
            loading={loading}
            loadingComplete={loadingComplete}
          />
        </div>
      </Container>
    </CreateContainer>
  )
}

export default NotificationSettings